<template>
  <b-container fluid class="p-0 mb-5">
    <b-row align="left" class="mb-4">
      <b-col cols="12">
        <h1>
          Device <code>{{ deviceId }}</code>
        </h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6" v-if="canGetDevice">
        <DeviceDetails
          v-if="!getIsLoading"
          :payload="device"
          :metadataMapping="metadataMapping"
          :deviceId="deviceId"
          :linkedAssets="linkedAssets"
          :canUpdateDevice="canUpdateDevice"
          :canGetDeviceMapping="canGetDeviceMapping"
          :canUnlinkAsset="canUnlinkAsset"
          :canMLinkAsset="canMLinkAsset"
          :canSearchAsset="canSearchAsset"
          @saveMetadata="onSaveMetadata"
          @saveInfos="onSaveDeviceInfos"
          @unlink="onUnlinkAsset"
          @link="onLinkAsset"
        />
      </b-col>
      <b-col cols="6" v-else>
        <b-card>
          <h1 class="float-left display-3 mr-4">
            <i class="fa fa-lock"></i>
          </h1>
          <h4 class="pt-3">You are not allowed to see device informations.</h4>
        </b-card>
      </b-col>
      <b-col cols="6" v-if="canSearchDeviceHistory">
        <b-card no-body class="h-100" v-if="!getIsLoading">
          <div
            v-if="
              device._source.measures.position &&
                device._source.measures.position.point
            "
            class="device-measures h-100"
          >
            <DeviceMap
              class="h-100"
              :device="device"
              :measures="tableMeasures"
              :fields="fields"
            />
          </div>
          <div v-else>
            <h4 class="mt-3 p-3">
              This device has no position.
            </h4>
          </div>
        </b-card>
      </b-col>
      <b-col cols="6" v-else>
        <b-card>
          <h1 class="float-left display-3 mr-4">
            <i class="fa fa-lock"></i>
          </h1>
          <h4 class="pt-3">You are not allowed to see device history.</h4>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import DeviceDetails from '@/components/device-manager/device/DeviceDetails';
import DeviceMap from '@/components/device-manager/device/DeviceMap.vue';
import { mapGetters } from 'vuex';
import {
  canGetDeviceMapping,
  canSearchAsset,
  canGetDevice,
  canSearchDeviceHistory,
  canMLinkAsset,
  canUnlinkAsset,
  canUpdateDevice
} from '@/helpers/rights.js';
import {
  tenantId,
  assetOrDevice as device,
  model,
  reference,
  measureTypes,
  currentMeasurePath,
  tableMeasures
} from '@/composition/asset-device';
import { lte } from 'lodash';

export default {
  name: 'Device',
  components: {
    DeviceDetails,
    DeviceMap
  },
  setup() {
    return {
      tenantId,
      model,
      reference,
      measureTypes,
      currentMeasurePath,
      tableMeasures,
      device
    };
  },
  data() {
    return {
      linkedAssets: [],
      metadataMapping: {},
      measures: [],
      fields: [
        {
          key: 'date',
          label: 'Date',
          sortable: false
        },
        {
          key: 'measure',
          label: 'Measure',
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    ...mapGetters('app', ['getIsLoading']),
    deviceId() {
      return _.get(this.$route, 'params.deviceId', null);
    },
    canGetDeviceMapping,
    canSearchAsset,
    canGetDevice,
    canSearchDeviceHistory,
    canMLinkAsset,
    canUnlinkAsset,
    canUpdateDevice
  },
  watch: {
    tenantId: {
      async handler(value) {
        if (value) {
          await this.initView();
        }
      },
      immediate: true
    }
  },
  beforeMount() {
    this.$store.commit('app/SET_LOADING');
  },
  methods: {
    async initView() {
      this.$store.commit('app/SET_LOADING');
      this.fetchDeviceMapping();
      await this.fetchDeviceInfos();
      await this.fetchLinkedAsset();
      this.$store.commit('app/UNSET_LOADING');
    },

    async fetchDeviceMapping() {
      if (!this.canGetDevicesMapping) {
        return;
      }
      try {
        const mapping = await this.$kuzzle.collection.getMapping(
          tenantId.value,
          'devices'
        );
        this.metadataMapping = mapping.properties.metadata || {};
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching device metadata mapping',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
    },
    async fetchLinkedAsset() {
      if (!this.canSearchAsset) {
        return;
      }
      let query = {
        equals: {
          _id: device.value._source.assetId
        }
      };
      try {
        const response = await this.$kuzzle.query({
          index: tenantId.value,
          controller: 'device-manager/asset',
          action: 'search',
          body: {
            query
          },
          size: 1,
          lang: 'koncorde'
        });
        this.linkedAssets = response.result.hits.map(d => ({
          id: d._source.assetId || d._id,
          measureTypes: Object.keys(_.get(d, '_source.measures', {})),
          reference: d._source.reference
        }));
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching linked assets',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
      }
    },
    async fetchDeviceInfos() {
      if (!this.canGetDevice) {
        // @TODO redirect to not allowed view
        return;
      }
      try {
        const deviceRes = await this.$kuzzle.document.get(
          tenantId.value,
          'devices',
          this.deviceId
        );
        device.value = deviceRes;
      } catch (error) {
        // @TODO redirect to 404 device not found
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching device information',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    onNumberOfPointsChanged(value) {
      this.perPage = value;
      this.fetchMeasures();
    },
    async onLinkAsset(asset) {
      if (!this.canMLinkAsset) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const result = await this.$kuzzle.query({
          controller: 'device-manager/device',
          action: 'mLink',
          body: {
            records: [{ deviceId: this.deviceId, assetId: asset }]
          },
          refresh: 'wait_for'
        });
        this.initView();
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while linking assets',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onUnlinkAsset(deviceId) {
      if (!this.canUnlinkAsset) {
        return;
      }
      const device = this.linkAsset;
      if (!device) {
        this.$log.warn(
          `Could not retrieve measure types from ${deviceId}. Unable to find it in the linked devices array.`
        );
      }
      const h = this.$createElement;
      const messageVNode = h('div', { class: ['foobar'] }, [
        h('p', { class: [] }, [
          'Unlink device ',
          h('code', deviceId),
          ' from device ',
          h('code', this.deviceId),
          ' ?'
        ]),
        device && device.measureTypes.length
          ? h('small', { class: [] }, [
              'This device provides measures of ',
              device.measureTypes.map(t => h('b-badge', { class: ['mr-1'] }, t))
            ])
          : h('div')
      ]);
      const value = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: 'Unlink confirmation',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
        okTitle: 'Unlink'
      });
      if (!value) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        await this.$kuzzle.query({
          index: tenantId.value,
          controller: 'device-manager/device',
          action: 'unlink',
          _id: deviceId,
          refresh: 'wait_for'
        });
        this.initView();
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while unlinking devices',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onSaveDeviceInfos(infos) {
      if (!this.canUpdateDevice) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      delete infos.type;
      try {
        const result = await this.$kuzzle.document.update(
          tenantId.value,
          'devices',
          this.deviceId,
          infos
        );
        device.value._source.model = infos.model;
        device.value._source.reference = infos.reference;
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating device',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onSaveMetadata(metadata) {
      if (!this.canUpdateDevice) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const result = await this.$kuzzle.document.update(
          tenantId.value,
          'devices',
          this.deviceId,
          {
            metadata
          }
        );
        device.value._source.metadata = metadata;
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating device',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    }
  }
};
</script>

<style>
.device-measures {
  display: flex;
  flex-direction: column;
}

.device-measures-tabcontent {
  flex-grow: 1;
}
</style>
